"use client";

import dynamic from "next/dynamic";
import { SLAccordionLight } from "@/components/other/SLAccordionLight";
import { useMessages } from "@/lib/i18n/useMessages";
import { LegalForm } from "@/lib/legalFormEnum";
import { LocationEnum } from "@/lib/locationEnum";
import { fillStringTemplate } from "@/lib/utils/fillStringTemplate";
import { Divider, Grid, Skeleton, Stack } from "@mui/material";
import { Fragment } from "react";

const CompaniesSparkLine = dynamic(
  () => import("@/components/charts/SLSparkLine"),
  {
    ssr: false,
    loading: () => <Skeleton variant="rounded" height={120} animation="wave" />,
  },
);

const IndustryBarChart = dynamic(() => import("@/components/charts/BarChart"), {
  ssr: false,
  loading: () => <Skeleton variant="rounded" height={240} animation="wave" />,
});

const SuburbBarChart = dynamic(
  () => import("@/components/charts/SuburbBarChart"),
  {
    ssr: false,
    loading: () => <Skeleton variant="rounded" height={240} animation="wave" />,
  },
);

export function StatisticsDetailAccordions({
  city,
  location,
  effectiveYear,
}: {
  city: string;
  location: LocationEnum;
  effectiveYear: number;
}) {
  const t = useMessages().common.expertiseStatistics;

  return (
    <>
      <Grid size={12}>
        <SLAccordionLight
          title={fillStringTemplate(t.accordionByLegalForm, {
            city,
            year: effectiveYear,
          })}
        >
          <Stack alignItems="center">
            <Stack direction="column" gap={4}>
              {Object.values(LegalForm).map((legalForm, idx) => (
                <Fragment key={legalForm}>
                  {idx > 0 && <Divider sx={{ mx: 4 }} />}

                  <CompaniesSparkLine
                    location={location}
                    year={effectiveYear}
                    legalForm={legalForm}
                  />
                </Fragment>
              ))}
            </Stack>
          </Stack>
        </SLAccordionLight>
      </Grid>
      <Grid size={12}>
        <SLAccordionLight
          title={fillStringTemplate(t.accordionByIndustry, {
            city,
            year: effectiveYear,
          })}
        >
          <IndustryBarChart location={location} year={effectiveYear} />
        </SLAccordionLight>
      </Grid>
      <Grid size={12}>
        <SLAccordionLight
          title={fillStringTemplate(t.accordionBySuburb, {
            city,
            year: effectiveYear,
          })}
        >
          <SuburbBarChart location={location} year={effectiveYear} />
        </SLAccordionLight>
      </Grid>
    </>
  );
}
